import { Button } from '@/components/ui/button';
import { createPageUrl } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/primelogictechsolutions.webp';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: 'Home' },
    { name: 'About Us', path: 'AboutUs' },
    { name: 'Services', path: 'Services' },
    { name: 'Contact', path: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-primary/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to={createPageUrl('Home')}
            className="flex items-center gap-3"
            onClick={() => setIsOpen(false)}
          >
            <img
              src={logo}
              alt="PrimeLogic Logo"
              className="h-10 w-auto"
              width="254"
              height="40"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive =
                location.pathname === createPageUrl(link.path) ||
                (location.pathname === '/' && link.path === 'Home');
              return (
                <Link
                  key={link.name}
                  to={createPageUrl(link.path)}
                  className={`${
                    isActive ? 'text-secondary' : 'text-slate-300'
                  } hover:text-secondary transition-colors duration-200 text-sm font-medium`}
                >
                  {link.name}
                </Link>
              );
            })}
            <Link to={createPageUrl('Contact')}>
              <Button className="bg-orange-700 hover:bg-orange-800 text-white font-semibold rounded-full px-6">
                Get Started
                <ChevronRight className="w-4 h-4 ml-1" aria-hidden="true" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <X className="w-6 h-6" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-primary/98 backdrop-blur-md border-t border-secondary/20"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => {
                const isActive =
                  location.pathname === createPageUrl(link.path) ||
                  (location.pathname === '/' && link.path === 'Home');
                return (
                  <button
                    key={link.name}
                    className={`block w-full text-left ${
                      isActive ? 'text-secondary' : 'text-primary-foreground/80'
                    } hover:text-secondary transition-colors py-2 text-lg`}
                    onClick={() => {
                      setIsOpen(false); // close first
                      setTimeout(() => {
                        navigate(createPageUrl(link.path)); // then navigate
                      }, 150);
                    }}
                  >
                    {link.name}
                  </button>
                );
              })}

              {/* Get Started Button (Mobile) */}
              <button
                className="w-full mt-4"
                onClick={() => {
                  setIsOpen(false);
                  setTimeout(() => {
                    navigate(createPageUrl('Contact'));
                  }, 150);
                }}
              >
                <Button className="w-full bg-secondary hover:bg-secondary text-white font-semibold rounded-full">
                  Get Started
                </Button>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
